import React, { Key, useEffect, useRef } from 'react';
import { Form, Table } from 'antd';
import { ColumnType, TableProps } from 'antd/es/table';
import { useMediaPredicate } from 'react-media-hook';
import { mapTableDataToFormEntries } from 'common/helpers/form.helper';
import { MEDIA_MOBILE_768 } from 'common/const/common.const';

interface IComponentProps<T extends { key: React.Key }> {
  className?: string;
  dataSource: T[];
  config: ColumnType<T>[];
  onFieldChange?: (key: Key, value: object) => void;
  onSort?: (field: string, order?: string) => void;
  summary?: (data: readonly T[]) => any;
  // MBC-80: optional fixed-height + sticky header. Pass-through to antd Table.scroll.
  scroll?: { y?: number | string };
  loadMore?: () => void;
  loading?: boolean;
  hasMore?: boolean;
}

export function DataTable<T extends { key: React.Key }>({
  className,
  dataSource,
  config,
  onFieldChange,
  onSort,
  summary,
  scroll,
  loadMore,
  loading = false,
  hasMore = true,
}: IComponentProps<T>) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const loaderRef = useRef<HTMLDivElement | null>(null);
  const loadMoreRef = useRef(loadMore);
  loadMoreRef.current = loadMore;

  const [form] = Form.useForm();
  const isMobile: boolean = useMediaPredicate(MEDIA_MOBILE_768);

  useEffect(() => {
    const entries = mapTableDataToFormEntries(dataSource);

    entries.forEach((entry) => {
      entry.forEach(([key, value]) => {
        form.setFieldValue(key, value);
      });
    });
  }, [dataSource]);

  useEffect(() => {
    if (!loadMore || loading || !hasMore) {
      return;
    }

    const container = containerRef.current;
    const loader = loaderRef.current;

    if (!container || !loader) {
      return;
    }

    let skipInitial = true;
    const observer = new IntersectionObserver(
      (entries) => {
        if (skipInitial) {
          skipInitial = false;
          return;
        }
        if (entries[0].isIntersecting) {
          loadMoreRef.current?.();
        }
      },
      { root: container, rootMargin: '200px' }
    );

    observer.observe(loader);

    return () => observer.disconnect();
  }, [!!loadMore, loading, hasMore]);

  const handleFiledChange = (value: { [key: Key]: any }) => {
    const entries = Object.entries(value)[0];
    const key = entries[0];
    const result = entries[1];

    onFieldChange?.(key, result);
  };

  const handleSort: TableProps<T>['onChange'] = (p, f, sorter) => {
    // @ts-ignore
    onSort?.(sorter.field, sorter.order);
  };

  return (
    <div
      ref={containerRef}
      className={`data-table__wrapper ${isMobile ? 'data-table__mob-wrapper' : ''} scrollbar-y ${className}`}
      style={{ ...(scroll?.y && { maxHeight: scroll?.y }) }}
    >
      <Form form={form} component={false} onValuesChange={handleFiledChange}>
        <Table<T>
          className={`data-table ${isMobile ? 'data-table__mob' : ''}`}
          pagination={false}
          dataSource={dataSource}
          columns={config}
          summary={summary}
          onChange={handleSort}
        />
      </Form>

      <div ref={loaderRef} style={{ height: 1 }} />
    </div>
  );
}
